import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LabBackdrop, LabNotifyForm } from "@/components/LabShell";
import { WAITLIST_TAG } from "@/labConfig";
import usePageMeta from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AMOUNTS = [18, 36, 72, 180];

export default function ComingSoon() {
  usePageMeta({
    title: "Launching soon",
    description:
      "The Jewish Music Lab is launching soon — free, playful experiments that turn Hebrew letters into music. Leave your email and hear about it on opening day.",
    path: "/",
  });
  const [amount, setAmount] = useState(null);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  const give = async () => {
    const value = Number(custom || amount);
    if (!value || value < 2) {
      toast.error("Pick an amount of $2 or more.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/donate/checkout`, {
        amount: value,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open checkout.");
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen relative flex flex-col"
      style={{ backgroundColor: "#FAF8F5", color: "#1A1523", fontFamily: "'Nunito', ui-sans-serif, system-ui, sans-serif" }}
      data-testid="coming-soon-page"
    >
      <LabBackdrop />
      <Toaster position="bottom-right" richColors />

      <main className="relative z-10 flex-1 w-full max-w-3xl mx-auto px-5 sm:px-6 py-16 sm:py-24">
        <img
          src="/lab-logo.png"
          alt="Jewish Music Lab"
          className="mb-8"
          style={{ width: 76, height: 76, objectFit: "contain" }}
          data-testid="coming-soon-logo"
        />

        <p
          className="text-xs uppercase mb-4"
          style={{ color: "#8C8198", fontFamily: "'Courier Prime', monospace", letterSpacing: "0.22em" }}
        >
          Opening soon
        </p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-[30ch]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
          data-testid="coming-soon-title"
        >
          The{" "}
          <span className="whitespace-nowrap" style={{ color: "#D99B26" }}>
            Jewish Music Lab
          </span>{" "}
          is launching soon.
        </h1>
        <p className="mt-6 text-base sm:text-lg max-w-[56ch]" style={{ color: "#5C5268", lineHeight: 1.8 }}>
          <a
            href="https://danberrymusic.com"
            target="_blank"
            rel="noreferrer"
            data-testid="coming-soon-dan-link"
            className="underline underline-offset-4 decoration-[#D99B26] transition-colors hover:text-[#E86F4A]"
            style={{ color: "#1A1523", fontWeight: 600 }}
          >
            Dan Berry
          </a>{" "}
          is creating a room full of musical experiments built on Hebrew letters, Torah and
          curiosity. Every letter of the aleph-bet gets its own note — so a name, a blessing,
          a verse turns into something you can actually hear and play with.
        </p>
        <p className="mt-4 text-base sm:text-lg max-w-[56ch]" style={{ color: "#5C5268", lineHeight: 1.8 }}>
          Free to play, nothing to install. The doors open shortly.
        </p>

        <div
          className="mt-10 rounded-2xl p-6 sm:p-7"
          style={{ backgroundColor: "#FFFFFF", border: "1px solid #EBE3D5" }}
        >
          <h2 className="text-xl sm:text-2xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
            Be there when it opens.
          </h2>
          <p className="text-sm mb-5" style={{ color: "#5C5268", lineHeight: 1.7 }}>
            Leave your name and email and you&apos;ll hear from us the day the doors open —
            plus the occasional note from the lab.
          </p>
          <LabNotifyForm
            testPrefix="coming-soon-notify"
            source="jml_coming_soon"
            extraTags={[WAITLIST_TAG]}
            cta="Let me know"
            doneText="✓ You're on the list. We'll write you the day the lab opens."
          />
        </div>

        <div
          className="mt-6 rounded-2xl p-6 sm:p-7"
          style={{ backgroundColor: "#FFF0EA", border: "1px solid #F6D8C9" }}
          data-testid="coming-soon-support"
        >
          <h2 className="text-xl sm:text-2xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
            Help bring it to life.
          </h2>
          <p className="text-sm mb-5 max-w-[56ch]" style={{ color: "#5C5268", lineHeight: 1.7 }}>
            A one-time gift covers the hosting, the long nights of building, and the next
            experiment nobody asked for yet.
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {AMOUNTS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => {
                  setAmount(a);
                  setCustom("");
                }}
                data-testid={`coming-soon-amount-${a}`}
                className="rounded-full px-5 h-11 text-sm transition-colors"
                style={{
                  backgroundColor: amount === a && !custom ? "#E86F4A" : "#FFFFFF",
                  color: amount === a && !custom ? "#FFFFFF" : "#5C5268",
                  border: "1px solid #F0C3AF",
                  fontFamily: "'Courier Prime', monospace",
                }}
              >
                ${a}
              </button>
            ))}
            <Input
              type="number"
              min="2"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Other"
              data-testid="coming-soon-amount-custom"
              className="h-11 w-28 bg-white border-[#F0C3AF] text-[#1A1523] placeholder:text-[#B59C93]"
            />
          </div>
          <Button
            onClick={give}
            disabled={busy}
            data-testid="coming-soon-give"
            className="h-11 rounded-full px-7 bg-[#E86F4A] hover:bg-[#D45E3A] text-white"
            style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.14em" }}
          >
            {busy ? "Opening…" : "GIVE ONCE"}
          </Button>
          <p className="mt-3 text-xs" style={{ color: "#8C8198" }}>
            Secure checkout by Stripe. One payment, nothing recurring.
          </p>
        </div>
      </main>

      <footer className="relative z-10 border-t" style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-7 text-xs" style={{ color: "#8C8198" }}>
          Built by{" "}
          <a
            href="https://danberrymusic.com"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2"
            data-testid="coming-soon-danberry"
          >
            Dan Berry
          </a>{" "}
          · <Link to="/privacy" className="underline underline-offset-2">Privacy</Link> ·{" "}
          <Link to="/terms" className="underline underline-offset-2">Terms</Link>
        </div>
      </footer>
    </div>
  );
}
